import React from 'react'
import { useTranslation } from 'react-i18next'
import { User } from 'lucide-react'
import talbetIcon from '../../assets/images/talbet.svg'
import Frame1Icon from '../../assets/images/Frame1.svg'
import home1Icon from '../../assets/images/home1.svg'
import carIcon from '../../assets/images/car.svg'

interface OrderConfirmationHeaderProps {
  order: any
  isRTL: boolean
}

const OrderConfirmationHeader: React.FC<OrderConfirmationHeaderProps> = ({
  order,
  isRTL
}) => {
  const { t } = useTranslation()
  
  // Extract table name/number - check both name and number fields with type safety
  const tableName = typeof order?.table?.name === 'string' ? order.table.name : (typeof order?.table_name === 'string' ? order.table_name : '')
  const tableNumber = typeof order?.table?.number === 'string' ? order.table.number : (typeof order?.table_number === 'string' || typeof order?.table_number === 'number' ? String(order.table_number) : (typeof order?.tableNumber === 'string' || typeof order?.tableNumber === 'number' ? String(order.tableNumber) : ''))
  const tableDisplay = tableName || tableNumber || ''
  
  // Map typeId to order type key
  const typeId = order?.type_id || order?.type?.id
  const getOrderTypeKey = (id: number | string | undefined): string => {
    const typeMap: Record<string, string> = {
      '1': 'delivery',
      '2': 'takeaway',
      '3': 'dine_in',
      '4': 'car_pickup',
      'takeaway': 'takeaway',
      'dine_in': 'dine_in',
      'delivery': 'delivery',
      'car_pickup': 'car_pickup'
    }
    const orderTypeName = typeof order?.type?.name === 'string' ? order.type.name : (typeof order?.type_name === 'string' ? order.type_name : (typeof order?.order_type === 'string' ? order.order_type : ''))
    const lowerName = String(orderTypeName).toLowerCase()
    
    if (lowerName.includes('dine_in') || lowerName.includes('dine') || lowerName.includes('local') || lowerName.includes('table')) {
      return 'dine_in'
    }
    if (lowerName.includes('takeaway') || lowerName.includes('take away')) {
      return 'takeaway'
    }
    if (lowerName.includes('delivery')) {
      return 'delivery'
    }
    if (lowerName.includes('car_pickup') || lowerName.includes('car pickup')) {
      return 'car_pickup'
    }
    
    return typeMap[String(id)] || typeMap[lowerName] || 'dine_in'
  }
  const orderTypeKey = getOrderTypeKey(typeId)
  const orderType = t(`order_type.${orderTypeKey}`)
  
  // Get icon based on order type
  const getOrderTypeIcon = (): string => {
    if (orderTypeKey === 'delivery') {
      return talbetIcon
    }
    if (orderTypeKey === 'takeaway') {
      return Frame1Icon
    }
    if (orderTypeKey === 'dine_in') {
      return home1Icon
    }
    if (orderTypeKey === 'car_pickup') {
      return carIcon
    }
    // Default to home1 for local
    return home1Icon
  }
  
  // Extract customer info - prioritize customer over user, only show if customer exists with type safety
  const customerName = typeof order?.customer?.name === 'string' ? order.customer.name : (typeof order?.customer_name === 'string' ? order.customer_name : (typeof order?.user?.name === 'string' ? order.user.name : ''))
  const customerPhone = typeof order?.customer?.phone === 'string' ? order.customer.phone : (typeof order?.customer_phone === 'string' ? order.customer_phone : (typeof order?.user?.phone === 'string' ? order.user.phone : ''))
  const hasCustomerInfo = customerName || customerPhone

  // Format order type and table display
  // Format: "محلي /44" - display table name directly from API without adding 't' prefix
  const formatTableDisplay = (table: string): string => {
    if (!table) return ''
    return `/${table}`
  }

  const orderTypeDisplay = tableDisplay && orderTypeKey === 'dine_in'
    ? `${t('order_type.dine_in')} ${formatTableDisplay(tableDisplay)}`
    : orderType

  return (
    <div className="w-full flex justify-between items-end pb-2">
      {/* Customer Info - Right side */}
      {hasCustomerInfo && (
        <div className="flex items-start gap-3">
          <div className="w-[50px] h-[50px] bg-neutral-100 rounded-xl flex items-center justify-center">
            <User className="w-6 h-6 text-gray-600" />
          </div>
          <div className="flex flex-col justify-start items-start gap-1">
            {customerName && (
              <div className=" text-text-primary text-xs font-normal font-['IBM_Plex_Sans_Arabic']">
                {customerName}
              </div>
            )}
            {customerPhone && (
              <div className="text-right text-text-secondary text-xs font-normal font-['IBM_Plex_Sans_Arabic']">
                {customerPhone}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Order Type/Table - Left side */}
      <div className="flex items-center gap-1">
        <img src={getOrderTypeIcon()} alt="" className="w-5 h-5" />
        <div className="text-right text-text-primary text-base font-bold font-['IBM_Plex_Sans_Arabic']">
          {orderTypeDisplay}
        </div>
      </div>
    </div>
  )
}

export default OrderConfirmationHeader

